/**
 * File Service - Manages project files and folder navigation
 * Provides API calls for fetching files associated with projects
 */

import { API_BASE_URL } from '../API/api';
import axios from 'axios';
import { getDownloadsContext } from '../contexts/DownloadsContext';
import { authHeaders } from '../utils/authHeader';
import {
  sanitizeDownloadNamePrefix,
  prefixedDownloadName,
  deliverablesZipName,
  fileNameFromContentDisposition,
} from '../utils/deliverableDownloadNames';

class FileService {
  constructor() {
    console.log('📁 FileService initialized');
    this.usersCache = new Map(); // Cache for user data
  }

  /**
   * Name to save a zip under. With a Deliverables `namePrefix` the backend names
   * the zip ("<prefix> deliverables.zip"); we honour its Content-Disposition when
   * the header is readable (same-origin via CloudFront) and otherwise compute the
   * identical default. Without a prefix the caller's name is used as before.
   * @private
   */
  _resolveZipDownloadName(response, requestedName, namePrefix) {
    if (!namePrefix) {
      return requestedName;
    }
    const fromHeader = fileNameFromContentDisposition(response?.headers?.['content-disposition']);
    return fromHeader || deliverablesZipName(namePrefix);
  }

  /**
   * Normalize MySQL google_drive_id values.
   * Treat sentinel/empty values as null (e.g. 0, ""0"", """", ""null"").
   */
  normalizeGoogleDriveId(rawValue) {
    if (rawValue === null || rawValue === undefined) return null;
    const value = String(rawValue).trim();
    if (!value) return null;
    const lower = value.toLowerCase();
    if (lower === '0' || lower === 'null' || lower === 'none' || lower === 'undefined') {
      return null;
    }
    return value;
  }

  /**
   * Fetch all users from the database
   * @returns {Promise<Map>} Map of user ID to user data
   */
  async getUsers() {
    console.log('👥 getUsers called');
    
    // Return cached data if available
    if (this.usersCache.size > 0) {
      console.log('👥 Returning cached users:', this.usersCache.size, 'users');
      return this.usersCache;
    }
    
    try {
      // Requires a login since 2026-10-19 (it used to publish every account).
      const response = await fetch(`${API_BASE_URL}/api/users`, { headers: await authHeaders() });
      console.log('👥 Users API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('👥 Users API Response data:', data);
      
      if (data.users) {
        // Cache the users data
        data.users.forEach(user => {
          this.usersCache.set(user.id, {
            id: user.id,
            email: user.email,
            fname: user.fname,
            lname: user.lname,
            fullName: `${user.fname || ''} ${user.lname || ''}`.trim()
          });
        });
        
        console.log('✅ Cached users:', this.usersCache.size, 'users');
        return this.usersCache;
      } else {
        console.warn('⚠️ No users in response data');
        return new Map();
      }
    } catch (error) {
      console.error('❌ Error in getUsers:', error);
      return new Map();
    }
  }

  /**
   * Get user name by ID
   * @param {number} userId - The user ID
   * @returns {string} Full name or 'Unknown' if not found
   */
  getUserName(userId) {
    const user = this.usersCache.get(userId);
    return user ? user.fullName : 'Unknown';
  }

  /**
   * Fetch all files for a specific project
   * @param {number} projectId - The 4-digit project ID
   * @returns {Promise<Array>} Array of file objects
   */
  async getProjectFiles(projectId) {
    console.log('📁 getProjectFiles called for project:', projectId);
    
    try {
      // First, fetch users to get owner names
      await this.getUsers();
      
      const response = await fetch(`${API_BASE_URL}/api/projects/${projectId}/files`);
      console.log('📁 API Response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('📁 API Response data:', data);
      
      if (data.files) {
        const formattedFiles = data.files.map(file => this.formatFileForFrontend(file));
        console.log('✅ Formatted files:', formattedFiles.length, 'items');
        return formattedFiles;
      } else {
        console.warn('⚠️ No files in response data');
        return [];
      }
    } catch (error) {
      console.error('❌ Error in getProjectFiles:', error);
      throw error;
    }
  }

  /**
   * Convert file data from database format to frontend format
   * @param {Object} dbFile - File data from database
   * @returns {Object} Formatted file object for frontend
   */
  formatFileForFrontend(dbFile) {
    console.log('🔧 formatFileForFrontend called with:', dbFile);
    
    const formatted = {
      id: dbFile.id,
      name: dbFile.name,
      isFolder: dbFile.isfolder === 1,
      size: dbFile.size || 0,
      projectId: dbFile.projects_id,
      parentId: dbFile.parent_id,
      ownerId: dbFile.owner_id,
      ownerName: this.getUserName(dbFile.owner_id), // Get full name from users cache
      isDeleted: dbFile.isdeleted === 1,
      modified: dbFile.modified ? new Date(dbFile.modified) : new Date(),
      created: dbFile.created ? new Date(dbFile.created) : new Date(),
      // Link to Google Drive folder: when set, children are loaded from Drive
      googleDriveId: this.normalizeGoogleDriveId(dbFile.google_drive_id),
      // S3 object key for unique-storage projects (Deliverables subtree, visualization, RSA outputs)
      s3Key: dbFile.s3_key != null && dbFile.s3_key !== '' ? String(dbFile.s3_key) : null,
      // Additional computed properties
      isRoot: String(dbFile.parent_id) === String(dbFile.projects_id),
      hasChildren: false, // Will be computed when building tree
      children: [], // Will be populated when building tree
      level: 0, // Will be computed when building tree
      expanded: false // For UI state
    };
    
    console.log('🔧 Formatted file:', formatted);
    return formatted;
  }

  /**
   * Build a hierarchical tree structure from flat file list
   * @param {Array} files - Array of formatted file objects
   * @param {number} projectId - The project ID
   * @returns {Array} Tree structure with root folders
   */
  buildFileTree(files, projectId) {
    console.log('[file-tree] buildFileTree called with:', files.length, 'files for project:', projectId);

    // Filter out deleted files and files not belonging to this project
    const projectIdKey = String(projectId);
    const validFiles = files.filter((file) => !file.isDeleted && file.projectId != null && String(file.projectId) === projectIdKey);
    console.log('[file-tree] valid files after filtering:', validFiles.length);

    // If a folder is linked to Google Drive, show that branch from Drive only.
    // Ignore legacy MySQL/S3 descendants to avoid mixed sources in the explorer.
    const validFilesById = new Map();
    validFiles.forEach((file) => {
      validFilesById.set(String(file.id), file);
    });

    const isUnderDriveLinkedFolder = (file) => {
      let parentId = file.parentId;
      const visited = new Set();

      while (parentId !== null && parentId !== undefined) {
        const parentKey = String(parentId);
        if (visited.has(parentKey)) {
          break;
        }
        visited.add(parentKey);

        const parent = validFilesById.get(parentKey);
        if (!parent) {
          break;
        }

        if (parent.isFolder && parent.googleDriveId) {
          return true;
        }

        if (String(parent.id) === String(parent.parentId)) {
          break;
        }

        parentId = parent.parentId;
      }

      return false;
    };

    const treeFiles = validFiles.filter((file) => !isUnderDriveLinkedFolder(file));
    console.log('[file-tree] files after pruning Drive-linked descendants:', treeFiles.length);

    // Create a map for quick lookup (folders with googleDriveId show expand arrow; children loaded on demand)
    const fileMap = new Map();
    treeFiles.forEach((file) => {
      const hasDriveLink = !!(file.googleDriveId && file.isFolder);
      fileMap.set(String(file.id), {
        ...file,
        children: [],
        hasChildren: hasDriveLink,
        level: 0,
        expanded: false,
      });
    });

    // Build the tree structure
    let rootFiles = [];

    treeFiles.forEach((file) => {
      const fileNode = fileMap.get(String(file.id));

      if (file.isRoot) {
        // This is a root-level file/folder
        fileNode.level = 0;
        rootFiles.push(fileNode);
      } else {
        // This file has a parent
        const parent = fileMap.get(String(file.parentId));
        if (parent) {
          parent.children.push(fileNode);
          parent.hasChildren = true;
          fileNode.level = parent.level + 1;
        } else {
          // Parent not found, treat as root
          console.warn('Parent not found for file:', file.name, 'parentId:', file.parentId);
          fileNode.level = 0;
          rootFiles.push(fileNode);
        }
      }
    });

    // Sort folders first, then files, alphabetically
    const sortFiles = (files) => {
      return files.sort((a, b) => {
        if (a.isFolder && !b.isFolder) return -1;
        if (!a.isFolder && b.isFolder) return 1;
        return a.name.localeCompare(b.name);
      });
    };

    // Recursively sort all levels
    const sortTree = (files) => {
      files.forEach((file) => {
        if (file.children.length > 0) {
          file.children = sortFiles(sortTree(file.children));
        }
      });
      return sortFiles(files);
    };

    // Rows with parent_id == projects_id land here as root-level siblings of the
    // project's root folder. Nothing renders them: the explorer's right pane shows
    // only `currentFolder.children`, and "navigate to root" selects the first root
    // FOLDER, so loose root-level files are in the tree but permanently undrawable.
    // Adopt them into the project root folder — the node the user sees as the top
    // of the project — so they appear where they are expected to be.
    const projectRootFolder = rootFiles.find(
      (item) => item.isFolder && (item.parentId === null || item.parentId === undefined)
    );
    if (projectRootFolder) {
      const adopted = rootFiles.filter((item) => item !== projectRootFolder && !item.isFolder);
      if (adopted.length > 0) {
        adopted.forEach((item) => {
          item.level = (projectRootFolder.level ?? 0) + 1;
          projectRootFolder.children.push(item);
        });
        projectRootFolder.hasChildren = true;
        rootFiles = rootFiles.filter((item) => !adopted.includes(item));
        console.log(
          '[file-tree] adopted', adopted.length,
          'root-level file(s) into project root folder', projectRootFolder.id
        );
      }
    }

    const sortedTree = sortTree(rootFiles);
    console.log('[file-tree] built file tree with', sortedTree.length, 'root items');

    return sortedTree;
  }

  /**
   * Fetch contents (files and folders) of a Google Drive folder for display in the file tree.
   * @param {number} projectId - The project ID (for API route)
   * @param {string} driveFolderId - The Google Drive folder ID
   * @returns {Promise<Array>} Array of items in same shape as file list (id, name, isFolder, isGoogleDriveItem, etc.)
   */
  async getGoogleDriveContents(projectId, driveFolderId) {
    const url = `${API_BASE_URL}/api/projects/${projectId}/google-drive-contents?folder_id=${encodeURIComponent(driveFolderId)}`;
    const response = await fetch(url);
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    const data = await response.json();
    return data.items || [];
  }

  /**
   * Prefetch the entire Drive folder tree for a project in one request.
   * The backend fetches all sub-folders in parallel, caches each in Redis,
   * and returns a flat map { googleDriveId: items[] } for instant navigation.
   * @param {number} projectId
   * @param {string[]} rootDriveIds - Drive IDs of the top-level linked folders (Source, Deliverables, etc.)
   * @returns {Promise<Object>} Map of { googleDriveId: items[] }
   */
  async prefetchDriveTree(projectId, rootDriveIds) {
    if (!rootDriveIds || rootDriveIds.length === 0) return {};
    const url = `${API_BASE_URL}/api/projects/${projectId}/google-drive-prefetch?root_ids=${encodeURIComponent(rootDriveIds.join(','))}`;
    try {
      const response = await fetch(url);
      if (!response.ok) return {};
      const data = await response.json();
      return data.folders || {};
    } catch {
      return {};
    }
  }

  /**
   * Current Google Drive folder name (metadata only) — matches Drive after renames.
   * @param {number} projectId
   * @param {string} driveFolderId
   * @returns {Promise<{ id: string, name: string }|null>}
   */
  async getGoogleDriveFolderMeta(projectId, driveFolderId) {
    if (!driveFolderId) {
      return null;
    }
    const url = `${API_BASE_URL}/api/projects/${projectId}/google-drive-folder-meta?folder_id=${encodeURIComponent(driveFolderId)}`;
    const response = await fetch(url);
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    const data = await response.json();
    return data && data.id && data.name ? { id: data.id, name: data.name } : null;
  }

  /**
   * Find a file by ID in the tree structure
   * @param {Array} tree - The file tree
   * @param {number} fileId - The file ID to find
   * @returns {Object|null} The file object or null if not found
   */
  findFileInTree(tree, fileId) {
    for (const file of tree) {
      if (file.id === fileId) {
        return file;
      }
      if (file.children.length > 0) {
        const found = this.findFileInTree(file.children, fileId);
        if (found) return found;
      }
    }
    return null;
  }

  /**
   * Get the path to a file in the tree
   * @param {Array} tree - The file tree
   * @param {number} fileId - The file ID to find
   * @returns {Array} Array of file objects representing the path
   */
  getFilePath(tree, fileId) {
    const path = [];
    
    const findPath = (files, targetId, currentPath) => {
      for (const file of files) {
        const newPath = [...currentPath, file];
        if (file.id === targetId) {
          return newPath;
        }
        if (file.children.length > 0) {
          const found = findPath(file.children, targetId, newPath);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findPath(tree, fileId, []) || [];
  }

  /**
   * Format file size for display
   * @param {number} bytes - File size in bytes
   * @returns {string} Formatted file size
   */
  formatFileSize(bytes) {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on file type
   * @param {Object} file - File object
   * @returns {string} FontAwesome icon class
   */
  getFileIcon(file) {
    if (file.isFolder) {
      return 'fas fa-folder';
    }
    
    const extension = file.name.split('.').pop()?.toLowerCase();
    
    switch (extension) {
      case 'pdf':
        return 'fas fa-file-pdf';
      case 'doc':
      case 'docx':
        return 'fas fa-file-word';
      case 'xls':
      case 'xlsx':
        return 'fas fa-file-excel';
      case 'csv':
        return 'fa-solid fa-file-csv';
      case 'ppt':
      case 'pptx':
        return 'fas fa-file-powerpoint';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return 'fas fa-file-image';
      case 'mp4':
        return 'fa-solid fa-clapperboard';
      case 'avi':
      case 'mov':
        return 'fas fa-file-video';
      case 'mp3':
      case 'wav':
        return 'fas fa-file-audio';
      case 'zip':
      case 'rar':
      case '7z':
        return 'fas fa-file-archive';
      case 'txt':
        return 'fas fa-file-alt';
      case 'json':
        return 'fas fa-file-code';
      case 'js':
      case 'jsx':
      case 'ts':
      case 'tsx':
        return 'fas fa-file-code';
      case 'css':
        return 'fas fa-file-code';
      case 'html':
        return 'fas fa-file-code';
      case 'py':
        return 'fas fa-file-code';
      case 'java':
        return 'fas fa-file-code';
      case 'cpp':
      case 'c':
        return 'fas fa-file-code';
      default:
        return 'fa-solid fa-file';
    }
  }

  /**
   * Download a single file by ID
   * @param {number} fileId - The file ID
   * @param {string} fileName - The file name for the download
   * @param {number} projectId - The project ID (optional but recommended for unique storage)
   * @param {Function} progressCallback - Callback function (progress, status)
   * @param {Object} cancelTokenSource - Axios cancel token source for cancellation
   * @param {Object} options - { namePrefix }: project name to prefix the saved file
   *   name with (Deliverables downloads only — see utils/deliverableDownloadNames.js)
   * @returns {Promise<void>}
   */
  async downloadFile(fileId, fileName, projectId = null, progressCallback = null, cancelTokenSource = null, options = {}) {
    console.log('📥 downloadFile called for file:', fileId, fileName, 'project:', projectId);
    const namePrefix = sanitizeDownloadNamePrefix(options?.namePrefix);

    // Build URL with project_id query parameter if provided.
    // dl=1 makes the backend send Content-Disposition: attachment even for videos —
    // without it Chrome plays MP4s in the tab when the page origin (e.g. apex domain)
    // differs from API_BASE_URL, because the anchor download attribute is ignored
    // cross-origin and videos are served inline for the in-app player.
    let downloadUrl = `${API_BASE_URL}/api/files/${fileId}/download?dl=1`;
    if (projectId) {
      downloadUrl += `&project_id=${projectId}`;
    }
    if (namePrefix) {
      // Backend applies the same prefix to Content-Disposition (cross-origin case);
      // link.download below covers the same-origin case.
      downloadUrl += `&name_prefix=${encodeURIComponent(namePrefix)}`;
    }

    // Trigger immediate download using direct link - browser handles streaming natively
    // This allows the browser to start downloading immediately without waiting
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = prefixedDownloadName(namePrefix, fileName);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    console.log('🚀 Download triggered immediately - browser handling streaming');
    
    // Clean up link immediately
    setTimeout(() => {
      document.body.removeChild(link);
    }, 100);
    
    // Track progress separately using fetch (runs in parallel, doesn't block download)
    // Note: This creates a second request, but it's necessary for progress tracking
    // The browser's native download (from link.click above) will be faster
    if (progressCallback) {
      try {
        const controller = new AbortController();
        
        // Handle cancellation - if axios cancel token is provided, we can't easily convert it
        // but we can still abort the progress tracking fetch
        // Note: The browser's native download (from link.click) cannot be cancelled
        // This only cancels the progress tracking request
        
        const response = await fetch(downloadUrl, {
          signal: controller.signal,
          headers: { 'Accept': '*/*' }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const contentLength = response.headers.get('content-length');
        const total = contentLength ? parseInt(contentLength, 10) : null;
        
        const reader = response.body.getReader();
        let loaded = 0;
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          loaded += value.length;
          
          if (total) {
            const percentCompleted = Math.round((loaded * 100) / total);
            progressCallback(percentCompleted, 'downloading');
          } else if (loaded > 0) {
            // Total size unknown, estimate progress
            progressCallback(50, 'downloading');
          }
        }
        
        progressCallback(100, 'downloading');
        console.log('✅ Progress tracking completed');
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('📥 Progress tracking cancelled');
        } else {
          console.warn('⚠️ Progress tracking failed (download may still succeed):', error);
        }
        // Don't throw - download may still succeed even if progress tracking fails
      }
    }
  }

  /**
   * Download a single Google Drive file by Drive file ID.
   * @param {string} googleDriveId - Google Drive file ID
   * @param {string} fileName - Download file name
   * @param {Function} progressCallback - Callback function (progress, status)
   * @param {Object} options - { namePrefix }: project name to prefix the saved file name with
   * @returns {Promise<void>}
   */
  async downloadGoogleDriveFile(googleDriveId, fileName = 'downloaded_file', progressCallback = null, options = {}) {
    if (!googleDriveId) {
      throw new Error('Missing Google Drive file ID');
    }
    const namePrefix = sanitizeDownloadNamePrefix(options?.namePrefix);

    const downloadUrl = `${API_BASE_URL}/api/google-drive/file/${encodeURIComponent(googleDriveId)}/media`;
    const response = await axios.get(downloadUrl, {
      responseType: 'blob',
      ...(namePrefix ? { params: { name_prefix: namePrefix } } : {}),
      onDownloadProgress: (progressEvent) => {
        if (!progressCallback) {
          return;
        }
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          progressCallback(Math.min(100, Math.max(0, percentCompleted)), 'downloading');
        } else if (progressEvent.loaded > 0) {
          // Total unknown, indicate active progress.
          progressCallback(50, 'downloading');
        }
      }
    });
    const contentType = response.headers?.['content-type'] || 'application/octet-stream';
    const blob = response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType });

    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = prefixedDownloadName(namePrefix, fileName || `drive_${googleDriveId}`);
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    }, 100);

    if (progressCallback) {
      progressCallback(100, 'downloading');
    }
  }

  /**
   * Download multiple Google Drive files.
   * @param {Array<Object>} files - Array of frontend file objects
   * @param {Function} progressCallback - Callback function (progress, status)
   * @param {Object} options - { namePrefix } forwarded to downloadGoogleDriveFile
   * @returns {Promise<void>}
   */
  async downloadGoogleDriveFiles(files, progressCallback = null, options = {}) {
    const driveFiles = (Array.isArray(files) ? files : []).filter(
      (file) => !file?.isFolder && !!String(file?.googleDriveId || '').trim()
    );

    if (driveFiles.length === 0) {
      console.warn('No Google Drive files to download');
      return;
    }

    for (let i = 0; i < driveFiles.length; i += 1) {
      const file = driveFiles[i];
      await this.downloadGoogleDriveFile(
        file.googleDriveId,
        file.name || `drive_${file.googleDriveId}`,
        (fileProgress) => {
          if (!progressCallback) {
            return;
          }
          // Aggregate per-file progress into overall 0-100.
          const overall = ((i + (fileProgress / 100)) / driveFiles.length) * 100;
          progressCallback(Math.min(100, Math.max(0, Math.round(overall))), 'downloading');
        },
        options
      );
    }

    if (progressCallback) {
      progressCallback(100, 'downloading');
    }
  }

  /**
   * Download multiple Google Drive files as a ZIP archive.
   * Uses DownloadsPopup tracking (same behavior as DB ZIP downloads).
   * @param {Array<Object>} files - Array of frontend file objects
   * @param {string} zipFileName - Name for ZIP file
   * @param {Function} progressCallback - Callback function (progress, status)
   * @param {Object} cancelTokenSource - Axios cancel token source for cancellation
   * @param {boolean} skipTracking - If true, don't track in DownloadsContext
   * @param {Object} options - { namePrefix }: Deliverables project prefix. When set the
   *   body carries `name_prefix` and no `zip_name`, so the backend prefixes every
   *   entry and names the zip "<prefix> deliverables.zip".
   * @returns {Promise<void>}
   */
  async downloadGoogleDriveFilesAsZip(
    files,
    zipFileName = 'drive_files.zip',
    progressCallback = null,
    cancelTokenSource = null,
    skipTracking = false,
    options = {}
  ) {
    // Folders are kept: the backend expands each one with its Drive subtree.
    const driveFiles = (Array.isArray(files) ? files : []).filter(
      (file) => !!String(file?.googleDriveId || '').trim()
    );
    if (driveFiles.length === 0) {
      console.warn('No Google Drive files to zip');
      return;
    }

    const projectId = driveFiles[0]?.projectId;
    if (!projectId) {
      throw new Error('Missing project ID for Google Drive ZIP download');
    }

    const namePrefix = sanitizeDownloadNamePrefix(options?.namePrefix);
    const provisionalZipName = namePrefix ? deliverablesZipName(namePrefix) : zipFileName;

    const downloadsContext = getDownloadsContext();
    let downloadId = null;

    // Create cancel token if not provided
    let internalCancelToken = cancelTokenSource;
    if (!internalCancelToken) {
      const CancelToken = axios.CancelToken;
      internalCancelToken = CancelToken.source();
    }

    if (downloadsContext && !skipTracking) {
      downloadId = downloadsContext.addDownload({
        fileName: provisionalZipName,
        fileIds: driveFiles.map((f) => f.googleDriveId).filter(Boolean),
        status: 'creating',
        progress: 0,
        cancelTokenSource: internalCancelToken,
      });
    }

    try {
      if (progressCallback) {
        progressCallback(5, 'creating');
      }
      if (downloadsContext && downloadId && !skipTracking) {
        downloadsContext.updateDownload(downloadId, { progress: 5, status: 'creating' });
      }

      const payloadItems = driveFiles.map((file) => ({
        googleDriveId: file.googleDriveId,
        name: file.name || null,
        isFolder: !!file.isFolder,
      }));

      const response = await axios({
        url: `${API_BASE_URL}/api/projects/${projectId}/google-drive-files/download-zip`,
        method: 'POST',
        data: namePrefix
          ? { items: payloadItems, name_prefix: namePrefix }
          : { items: payloadItems, zip_name: zipFileName },
        responseType: 'blob',
        timeout: 600000,
        cancelToken: internalCancelToken ? internalCancelToken.token : undefined,
        headers: {
          'Content-Type': 'application/json',
        },
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);

            if (progressCallback && percentCompleted > 0) {
              const totalProgress = 85 + Math.round(percentCompleted * 0.15);
              progressCallback(Math.min(totalProgress, 100), 'downloading');
            }

            if (downloadsContext && downloadId && !skipTracking) {
              const totalProgress = 85 + Math.round(percentCompleted * 0.15);
              downloadsContext.updateDownload(downloadId, {
                progress: Math.min(totalProgress, 100),
                status: 'downloading',
              });
            }
          } else if (progressEvent.loaded > 0) {
            if (progressCallback) {
              progressCallback(85, 'downloading');
            }
            if (downloadsContext && downloadId && !skipTracking) {
              downloadsContext.updateDownload(downloadId, { progress: 85, status: 'downloading' });
            }
          }
        }
      });

      if (!response.data) {
        throw new Error('No data received from server');
      }

      const blob = new Blob([response.data], { type: 'application/zip' });
      if (blob.size === 0) {
        throw new Error('Received empty ZIP file from server');
      }

      const effectiveZipName = this._resolveZipDownloadName(response, zipFileName, namePrefix);

      if (downloadsContext && downloadId && !skipTracking) {
        downloadsContext.updateDownload(downloadId, {
          status: 'completed',
          progress: 100,
          fileName: effectiveZipName,
          blob,
        });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = effectiveZipName;
      link.style.display = 'none';
      document.body.appendChild(link);

      requestAnimationFrame(() => {
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 100);
      });
    } catch (error) {
      if (axios.isCancel(error)) {
        if (downloadsContext && downloadId && !skipTracking) {
          downloadsContext.removeDownload(downloadId);
        }
        throw error;
      }

      if (downloadsContext && downloadId && !skipTracking) {
        downloadsContext.updateDownload(downloadId, {
          status: 'error',
        });
      }

      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to download Google Drive ZIP: ${errorMessage}`);
    }
  }

  /**
   * Download multiple files as a ZIP archive
   * @param {Array} fileIds - Array of file IDs to download
   * @param {string} zipFileName - Name for the ZIP file
   * @param {Function} progressCallback - Callback function (progress, status)
   * @param {Object} cancelTokenSource - Axios cancel token source for cancellation
   * @param {boolean} skipTracking - If true, don't track this download in DownloadsContext (for re-downloads)
   * @param {Object} options - { namePrefix }: Deliverables project prefix. When set the
   *   body carries `name_prefix`, so the backend prefixes every entry and names the
   *   zip "<prefix> deliverables.zip".
   * @returns {Promise<void>}
   */
  async downloadFilesAsZip(selection, zipFileName = 'files.zip', progressCallback = null, cancelTokenSource = null, skipTracking = false, options = {}) {
    // `selection` is a list of file ids, or of { id, is_folder } items. A folder
    // item makes the backend zip the folder's whole subtree as "Folder/Sub/file".
    const items = (Array.isArray(selection) ? selection : [])
      .map((entry) => (entry !== null && typeof entry === 'object'
        ? { id: entry.id, is_folder: !!(entry.is_folder ?? entry.isFolder) }
        : { id: entry, is_folder: false }))
      .filter((entry) => entry.id !== undefined && entry.id !== null);
    const hasFolders = items.some((entry) => entry.is_folder);
    const fileIds = items.filter((entry) => !entry.is_folder).map((entry) => entry.id);
    console.log('📦 downloadFilesAsZip called for', items.length, 'items', hasFolders ? '(with folders)' : '', skipTracking ? '(skip tracking)' : '');
    const namePrefix = sanitizeDownloadNamePrefix(options?.namePrefix);
    // Per-project storage tables number file ids from 1, so an id is only unique
    // together with its project. Without this the backend scans every project's
    // table and packs whichever row has that id first.
    const projectId = Number.isInteger(Number(options?.projectId)) && Number(options?.projectId) > 0
      ? Number(options.projectId)
      : null;
    const provisionalZipName = namePrefix ? deliverablesZipName(namePrefix) : zipFileName;

    // Track download in DownloadsContext (unless skipTracking is true)
    const downloadsContext = getDownloadsContext();
    let downloadId = null;

    // Create cancel token if not provided
    let internalCancelToken = cancelTokenSource;
    if (!internalCancelToken) {
      const CancelToken = axios.CancelToken;
      internalCancelToken = CancelToken.source();
    }

    if (downloadsContext && !skipTracking) {
      downloadId = downloadsContext.addDownload({
        fileName: provisionalZipName,
        fileIds: fileIds,
        // Kept so a re-download from the Downloads popup carries the same prefix,
        // asks the same project for the same ids, and repeats any folders.
        namePrefix: namePrefix || null,
        projectId: projectId,
        ...(hasFolders ? { items } : {}),
        status: 'creating',
        progress: 0,
        cancelTokenSource: internalCancelToken,
      });
    }
    
    try {
      // Notify that ZIP creation is starting
      if (progressCallback) {
        progressCallback(5, 'creating');
      }
      
      if (downloadsContext && downloadId && !skipTracking) {
        downloadsContext.updateDownload(downloadId, { progress: 5, status: 'creating' });
      }
      
      const response = await axios({
        url: `${API_BASE_URL}/api/files/download-zip`,
        method: 'POST',
        data: {
          // file_ids stays the flat file list so a backend that predates folder
          // support still returns the selected files; items carries the folders.
          file_ids: fileIds,
          ...(hasFolders ? { items } : {}),
          ...(projectId ? { project_id: projectId } : {}),
          ...(namePrefix ? { name_prefix: namePrefix } : {}),
          ...(!namePrefix && options?.zipName ? { zip_name: options.zipName } : {}),
        },
        responseType: 'blob',
        timeout: 600000, // 10 minute timeout for large ZIPs
        cancelToken: internalCancelToken ? internalCancelToken.token : undefined,
        headers: {
          'Content-Type': 'application/json',
        },
        onDownloadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`📦 ZIP download progress: ${percentCompleted}%`);
            
            // When download starts, we know ZIP creation is done
            if (progressCallback && percentCompleted > 0) {
              // ZIP creation is done (85%), now downloading (85-100%)
              const totalProgress = 85 + Math.round(percentCompleted * 0.15);
              progressCallback(Math.min(totalProgress, 100), 'downloading');
            }
            
            // Update downloads context
            if (downloadsContext && downloadId && !skipTracking) {
              const totalProgress = 85 + Math.round(percentCompleted * 0.15);
              downloadsContext.updateDownload(downloadId, {
                progress: Math.min(totalProgress, 100),
                status: 'downloading',
              });
            }
          } else if (progressEvent.loaded > 0) {
            // Download has started but we don't know total size yet
            // ZIP creation is done, now downloading
            if (progressCallback) {
              progressCallback(85, 'downloading');
            }
            
            if (downloadsContext && downloadId && !skipTracking) {
              downloadsContext.updateDownload(downloadId, { progress: 85, status: 'downloading' });
            }
          }
        }
      });

      // Check if response has data
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      // Create blob and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      console.log('📦 Blob created, size:', blob.size, 'bytes', skipTracking ? '(skip tracking)' : '');
      
      if (blob.size === 0) {
        throw new Error('Received empty ZIP file from server');
      }

      const effectiveZipName = this._resolveZipDownloadName(response, zipFileName, namePrefix);

      // Store blob in downloads context for re-download (only if tracking)
      if (downloadsContext && downloadId && !skipTracking) {
        downloadsContext.updateDownload(downloadId, {
          status: 'completed',
          progress: 100,
          fileName: effectiveZipName,
          blob: blob,
        });
      }

      // Always trigger download, regardless of skipTracking
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = effectiveZipName;
      link.style.display = 'none';
      document.body.appendChild(link);

      console.log('🚀 Triggering download for:', effectiveZipName, 'Blob size:', blob.size, 'bytes');
      
      // Use a small delay to ensure the link is in the DOM
      requestAnimationFrame(() => {
        link.click();
        console.log('✅ Download link clicked');
        
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          console.log('✅ Cleanup completed');
        }, 100);
      });

      console.log('✅ ZIP file downloaded successfully:', effectiveZipName);
    } catch (error) {
      // Check if request was cancelled
      if (axios.isCancel(error)) {
        console.log('📦 ZIP download cancelled by user');
        
        // Update downloads context (only if tracking)
        if (downloadsContext && downloadId && !skipTracking) {
          downloadsContext.removeDownload(downloadId);
        }
        
        throw error; // Re-throw cancellation error without modification
      }
      console.error('❌ Error downloading ZIP file:', error);
      
      // Update downloads context with error (only if tracking)
      if (downloadsContext && downloadId && !skipTracking) {
        downloadsContext.updateDownload(downloadId, {
          status: 'error',
        });
      }
      
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to download ZIP: ${errorMessage}`);
    }
  }

  /**
   * Download multiple files
   * Best practice: Use ZIP for multiple files (2+), individual download for single file
   * @param {Array} files - Array of file objects to download
   * @param {Function} progressCallback - Callback function (progress, status)
   * @param {Object} cancelTokenSource - Axios cancel token source for cancellation
   * @param {Object} options - { namePrefix } forwarded to downloadFile / downloadFilesAsZip
   * @returns {Promise<void>}
   */
  async downloadFiles(files, progressCallback = null, cancelTokenSource = null, options = {}) {
    console.log('📥 downloadFiles called for', files.length, 'files');
    
    // Folders are downloaded too: the backend zips each one with its subtree.
    const filesToDownload = (Array.isArray(files) ? files : []).filter((file) => file && file.id !== undefined);
    
    if (filesToDownload.length === 0) {
      console.warn('⚠️ No files to download');
      return;
    }

    // Best practice: Use ZIP for multiple items or any folder, direct download for a single file
    if (filesToDownload.length === 1 && !filesToDownload[0].isFolder) {
      // Single file - download directly
      console.log('📥 Downloading single file...');
      if (progressCallback) {
        progressCallback(0, 'downloading');
      }
      // Pass projectId from file object to ensure correct table/bucket lookup
      await this.downloadFile(filesToDownload[0].id, filesToDownload[0].name, filesToDownload[0].projectId, progressCallback, cancelTokenSource, options);
      if (progressCallback) {
        progressCallback(100, 'downloading');
      }
    } else {
      // Multiple items, or a folder - create ZIP
      console.log(`📦 Downloading ${filesToDownload.length} items as ZIP...`);
      const selection = filesToDownload.map((file) => ({ id: file.id, is_folder: !!file.isFolder }));
      const singleFolder = filesToDownload.length === 1 ? filesToDownload[0] : null;
      const zipFileName = singleFolder
        ? `${String(singleFolder.name || `folder_${singleFolder.id}`).replace(/[\\/:*?"<>|]+/g, '-')}.zip`
        : `files_${filesToDownload.length}_items.zip`;
      // Same project scoping as the single-file branch above.
      const zipOptions = {
        ...options,
        projectId: options?.projectId || filesToDownload[0].projectId || null,
        ...(singleFolder ? { zipName: zipFileName } : {}),
      };
      await this.downloadFilesAsZip(selection, zipFileName, progressCallback, cancelTokenSource, false, zipOptions);
      if (progressCallback) {
        progressCallback(100, 'downloading');
      }
    }

    console.log('✅ All files download process completed');
  }

  /**
   * Create a new folder in a project
   * @param {number} projectId - The project ID
   * @param {string} folderName - The name of the folder to create
   * @param {number|null} parentId - The parent folder ID (null for root, defaults to projectId)
   * @returns {Promise<Object>} The created folder object
   */
  async createFolder(projectId, folderName, parentId = null, parentGoogleDriveId = null) {
    console.log(
      '📁 createFolder called for project:',
      projectId,
      'name:',
      folderName,
      'parent:',
      parentId,
      'parentGoogleDriveId:',
      parentGoogleDriveId
    );
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/projects/${projectId}/folders`,
        {
          name: folderName,
          parent_id: parentId || projectId,
          parent_google_drive_id: parentGoogleDriveId || null,
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('✅ Folder created successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error creating folder:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to create folder: ${errorMessage}`);
    }
  }

  /**
   * Soft delete files/folders (sets isdeleted=1 in MySQL)
   * @param {Array<number>} fileIds - Array of file IDs to delete
   * @returns {Promise<Object>} The delete response
   */
  async deleteFiles(fileIds) {
    console.log('🗑️ deleteFiles called for:', fileIds);
    
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/files/delete`, {
        data: {
          file_ids: fileIds
        },
        headers: await authHeaders({ 'Content-Type': 'application/json' })
      });
      
      console.log('✅ Files deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting files:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to delete files: ${errorMessage}`);
    }
  }

  /**
   * Delete Google Drive items for a project.
   * @param {number} projectId - Project ID
   * @param {Array<Object>} items - Array of selected drive items
   * @param {string|null} folderDriveId - Current folder drive ID
   * @returns {Promise<Object>} Delete response
   */
  async deleteGoogleDriveFiles(projectId, items, folderDriveId = null) {
    console.log('🗑️ deleteGoogleDriveFiles called for:', items);

    try {
      const payloadItems = (Array.isArray(items) ? items : []).map((item) => ({
        id: item?.id,
        googleDriveId: item?.googleDriveId || null,
        name: item?.name || null,
        isFolder: !!item?.isFolder,
        folder_drive_id: item?.parentDriveId || folderDriveId || null
      }));

      const response = await axios.delete(
        `${API_BASE_URL}/api/projects/${projectId}/google-drive-files/delete`,
        {
          data: {
            folder_drive_id: folderDriveId || null,
            items: payloadItems
          },
          headers: await authHeaders({ 'Content-Type': 'application/json' })
        }
      );

      console.log('✅ Google Drive files deleted successfully:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting Google Drive files:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to delete Google Drive files: ${errorMessage}`);
    }
  }

  /**
   * Delete objects from a project's dedicated S3 bucket under the source/ prefix.
   * Only valid for S3-source projects (SSM <name>_source_storage="s3"). Any key
   * outside source/ is rejected server-side.
   * @param {number} projectId
   * @param {Array<string>} keys  e.g. ["source/foo.las", "source/bar.laz"]
   * @returns {Promise<{success:boolean, deleted:string[], errors:Array}>}
   */
  async deleteS3SourceObjects(projectId, keys) {
    const keyList = (Array.isArray(keys) ? keys : []).map((k) => String(k || '').trim()).filter(Boolean);
    if (!projectId || keyList.length === 0) {
      throw new Error('projectId and at least one key are required');
    }
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/projects/${projectId}/source/s3-delete`,
        { keys: keyList },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting S3 source objects:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to delete S3 source objects: ${errorMessage}`);
    }
  }

  /**
   * Count + total bytes under an allowlisted prefix of the project's bucket
   * (images/, camera-data/, source/). Traffic Signs wizard panels.
   * @returns {Promise<{count:number, bytes:number, prefix:string, sourceStorage:string}>}
   */
  async getS3PrefixSummary(projectId, prefix) {
    if (!projectId || !prefix) throw new Error('projectId and prefix are required');
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/projects/${projectId}/source/s3-list`,
        { params: { prefix, summary: 1, _ts: Date.now() } }
      );
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching S3 prefix summary:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to get summary for ${prefix}: ${errorMessage}`);
    }
  }

  /**
   * Delete EVERY object under an allowlisted non-source prefix (images/,
   * camera-data/). The server refuses source/.
   * @returns {Promise<{success:boolean, deleted:string[], errors:Array}>}
   */
  async deleteS3Prefix(projectId, prefix) {
    if (!projectId || !prefix) throw new Error('projectId and prefix are required');
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/projects/${projectId}/source/s3-delete`,
        { prefix },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to clear ${prefix}: ${errorMessage}`);
    }
  }

  /**
   * Resolve a LAS/LAZ file whose source CRS couldn't be auto-detected: the user
   * supplied it via the modal, so the backend re-runs the reprojection trigger
   * with that hint and enqueues the job.
   * @param {number} projectId
   * @param {string} filename       e.g. "Track05.las"
   * @param {{epsg?:number, wkt?:string}} sourceCrs
   * @param {string} [targetWkt]    optional project-CRS override
   * @returns {Promise<Object>} trigger result ({status, run_id, ...})
   */
  async resolveReprojectCrs(projectId, filename, sourceCrs, targetWkt) {
    if (!projectId || !filename) {
      throw new Error('projectId and filename are required');
    }
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/projects/${projectId}/upload/reproject-resolve`,
        { filename, sourceCrs, ...(targetWkt ? { targetWkt } : {}) },
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      console.error('❌ Error resolving reprojection CRS:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to set source CRS: ${errorMessage}`);
    }
  }

  /**
   * Paste files/folders into a destination folder.
   * Supports both DB-backed items and Google Drive items (same-storage operations).
   * @param {number} projectId - Project ID
   * @param {Object} payload - Paste payload
   * @param {'copy'|'cut'} payload.operation - Clipboard operation
   * @param {Array<Object>} payload.items - Selected items from clipboard
   * @param {number|null} payload.destinationParentId - Destination DB folder id
   * @param {string|null} payload.destinationDriveId - Destination Drive folder id
   * @returns {Promise<Object>} Paste response
   */
  async pasteFiles(projectId, payload) {
    const {
      operation,
      items,
      destinationParentId = null,
      destinationDriveId = null,
    } = payload || {};

    if (!projectId) {
      throw new Error('projectId is required for paste');
    }

    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('Paste requires at least one item');
    }

    const normalizedItems = items.map((item) => ({
      id: item?.id ?? null,
      name: item?.name ?? null,
      isFolder: !!item?.isFolder,
      isGoogleDriveItem: !!item?.isGoogleDriveItem,
      googleDriveId: item?.googleDriveId || null,
      parentId: item?.parentId ?? null,
      parentDriveId: item?.parentDriveId || null,
    }));

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/projects/${projectId}/files/paste`,
        {
          operation,
          items: normalizedItems,
          destination_parent_id: destinationParentId,
          destination_drive_id: destinationDriveId || null,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('❌ Error pasting files:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to paste files: ${errorMessage}`);
    }
  }

  /**
   * Rename a single file/folder (DB or Google Drive item).
   * @param {number} projectId - Project ID
   * @param {Object} item - Item to rename
   * @param {string} newName - New item name
   * @returns {Promise<Object>} Rename response
   */
  async renameFile(projectId, item, newName) {
    if (!projectId) {
      throw new Error('projectId is required for rename');
    }

    const trimmedName = String(newName || '').trim();
    if (!trimmedName) {
      throw new Error('New name cannot be empty');
    }

    if (!item) {
      throw new Error('Item is required for rename');
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/projects/${projectId}/files/rename`,
        {
          item: {
            id: item?.id ?? null,
            name: item?.name ?? null,
            isFolder: !!item?.isFolder,
            isGoogleDriveItem: !!item?.isGoogleDriveItem,
            googleDriveId: item?.googleDriveId || null,
            parentId: item?.parentId ?? null,
            parentDriveId: item?.parentDriveId || null,
          },
          new_name: trimmedName,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('❌ Error renaming item:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to rename item: ${errorMessage}`);
    }
  }

  /**
   * Get cached RSA mileage totals by folder IDs.
   * @param {number} projectId - Project ID
   * @param {Array<string>} folderIds - Optional folder drive IDs
   * @returns {Promise<Object>} Mileage totals response
   */
  async getRsaMileageTotals(projectId, folderIds = []) {
    const params = new URLSearchParams();
    if (Array.isArray(folderIds) && folderIds.length > 0) {
      params.append('folder_ids', folderIds.join(','));
    }
    const query = params.toString();
    const url = `${API_BASE_URL}/api/projects/${projectId}/rsa-mileage-totals${query ? `?${query}` : ''}`;

    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching RSA mileage totals:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to fetch RSA mileage totals: ${errorMessage}`);
    }
  }

  /**
   * Get cached RSA ISD maneuver totals by folder IDs.
   * @param {number} projectId - Project ID
   * @param {Array<string>} folderIds - Optional folder drive IDs
   * @returns {Promise<Object>} ISD maneuver totals response
   */
  async getRsaIsdManeuverTotals(projectId, folderIds = []) {
    const params = new URLSearchParams();
    if (Array.isArray(folderIds) && folderIds.length > 0) {
      params.append('folder_ids', folderIds.join(','));
    }
    const query = params.toString();
    const url = `${API_BASE_URL}/api/projects/${projectId}/rsa-isd-maneuver-totals${query ? `?${query}` : ''}`;

    try {
      const response = await axios.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error fetching RSA ISD maneuver totals:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error';
      throw new Error(`Failed to fetch RSA ISD maneuver totals: ${errorMessage}`);
    }
  }
}

// Create and export a singleton instance
const fileService = new FileService();
export default fileService;
